import { datadogRum } from '@datadog/browser-rum';

interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  site: 'datadoghq.com' | 'datadoghq.eu' | 'us3.datadoghq.com' | 'us5.datadoghq.com' | 'ap1.datadoghq.com';
  service: string;
  env: string;
  version?: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  defaultPrivacyLevel: 'mask-user-input' | 'mask' | 'allow';
  beforeSend?: (event: any, context: any) => boolean;
}

// Regex pattern to extract service and version from remote app URLs
// This pattern matches URLs like:
// - https://staging.appcdn.leadconnectorhq.com/crm/conversations-components/remoteEntry.js
// - https://appcdn.leadconnectorhq.com/revex/phone-integration/remoteEntry.js
// - https://staging.appcdn.leadconnectorhq.com/leadgen/products/remoteEntry.js
// - https://staging.appcdn.leadconnectorhq.com/revex/gokollab-builder--ver--gokollab/remoteEntry.js
// - http://localhost:8080/js/chunk.5085197ea5782e62.js (for local development)
const REMOTE_APP_REGEX = /(?:appcdn\.leadconnectorhq\.com\/(?<service>[^\/]+)\/(?<version>[^\/]+?)(?:--ver--[^\/]+)?\/|localhost:\d+\/js\/chunk\.[a-f0-9]+\.js)/;

// Map of service names to their proper Datadog service names
const SERVICE_NAME_MAP: Record<string, string> = {
  'crm': 'highlevel-crm',
  'revex': 'highlevel-revex', 
  'leadgen': 'highlevel-leadgen',
  'automation': 'highlevel-automation',
  'ghl-micro-app': 'highlevel-ghl-micro-app',
  // Additional mappings based on remote apps configuration
  'conversations-components': 'highlevel-conversations',
  'conversations-new-ui': 'highlevel-conversations-v2',
  'phone-integration': 'highlevel-phone-integration',
  'custom-objects': 'highlevel-custom-objects',
  'ai-employees': 'highlevel-ai-employees',
  'integrations': 'highlevel-integrations',
  'auth': 'highlevel-auth',
  'memberships': 'highlevel-memberships',
  'clientportal-builder': 'highlevel-clientportal-builder',
  'communities-builder': 'highlevel-communities',
  'gokollab-builder': 'highlevel-gokollab',
  'certificates-builder': 'highlevel-certificates',
  'snapshots': 'highlevel-snapshots',
  'products': 'highlevel-products',
  'store-widgets': 'highlevel-store-widgets',
  'ecommerce': 'highlevel-ecommerce',
  'marketplace': 'highlevel-marketplace',
  'power-dialer': 'highlevel-power-dialer',
  'appointment-modal': 'highlevel-appointment-modal',
  'MediaEditor': 'highlevel-media-editor',
  'brand-boards': 'highlevel-brand-boards',
  'chat-widget': 'highlevel-chat-widget',
  'email-preview': 'highlevel-email-preview',
  'calendar-settings': 'highlevel-calendar-settings',
  'Onboarding': 'highlevel-onboarding'
};

/**
 * Extract service and version from stack trace for micro-frontend routing
 */
const extractServiceFromStack = (stack: string): { service?: string; version?: string } => {
  if (!stack) return {};
  
  const match = stack.match(REMOTE_APP_REGEX);
  
  // Handle localhost case for local development
  if (stack.includes('localhost:')) {
    return { 
      service: 'highlevel-spm-ts-local', 
      version: 'development' 
    };
  }
  
  if (!match?.groups) return {};
  
  const { service: rawService, version } = match.groups;
  
  // Use mapped service name if available, otherwise prefix with 'highlevel-'
  const service = SERVICE_NAME_MAP[rawService] || `highlevel-${rawService}`;
  
  return { service, version };
};

/**
 * BeforeSend callback for micro-frontend error routing
 */
const beforeSend = (event: any, context: any): boolean => {
  try {
    // Get stack trace from context or event
    const stack = context?.handlingStack || event?.error?.stack || event?.exception?.values?.[0]?.stacktrace?.frames?.map((f: any) => f.filename).join('\n');
    // console.log('stack', stack)
    if (stack) {
      const { service, version } = extractServiceFromStack(stack);
      
      if (service) {
        event.service = service;
        // console.log(`[Datadog] Routing error to service: ${service}${version ? ` (version: ${version})` : ''}`);
        
        // Add additional context for debugging
        if (process.env.NODE_ENV === 'development') {
          // console.log(`[Datadog] Stack trace excerpt:`, stack.substring(0, 200) + '...');
        }
      } else {
        // Log when no service is found for debugging
        if (process.env.NODE_ENV === 'development') {
          // console.log(`[Datadog] No service found in stack trace:`, stack.substring(0, 200) + '...');
        }
      }
      
      if (version) {
        event.version = version;
      }
    }
    
    return true;
  } catch (error) {
    console.error('[Datadog] Error in beforeSend:', error);
    return true; // Don't block the event if there's an error in processing
  }
};

// Environment-specific configuration
const getDatadogConfig = (config?: any): DatadogConfig => {
  const env = config?.mode || process.env.NODE_ENV || 'development';
  
  return {
    applicationId: 'c4f9974a-d568-4b45-ae4b-707509ce8ec5',
    clientToken: 'pub3db90b841ed9c2ba1ba188a3ddb49f29',
    site: 'datadoghq.com',
    service: 'spm-ts',
    env: env,
    version: process.env.BUILD_VERSION || 'development',
    sessionSampleRate: env === 'production' ? 100 : 100, // 100% in all environments for now
    sessionReplaySampleRate: env === 'production' ? 20 : 100, // Higher in dev for testing
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend,
  };
};

/**
 * Initialize Datadog RUM
 */
export function initializeDatadog(appConfig?: any): void {
  try {
    const config = getDatadogConfig(appConfig);
    const env = appConfig?.mode || process.env.NODE_ENV || 'development';
    
    console.log('[Datadog] Initializing with config:', {
      service: config.service,
      env: env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate
    });

    datadogRum.init(config);
    
    console.log('[Datadog] Initialized successfully');
  } catch (error) {
    console.error('[Datadog] Initialization failed:', error);
  }
}

/**
 * Add custom context to Datadog RUM
 */
export function addDatadogContext(context: Record<string, any>): void {
  try {
    if (datadogRum) {
      Object.entries(context).forEach(([key, value]) => {
        datadogRum.addAction(key, value);
      });
    }
  } catch (error) {
    console.error('[Datadog] Failed to add context:', error);
  }
}

/**
 * Track custom events in Datadog RUM
 */
export function trackDatadogEvent(eventName: string, attributes?: Record<string, any>): void {
  try {
    if (datadogRum) {
      datadogRum.addAction(eventName, attributes || {});
    }
  } catch (error) {
    console.error('[Datadog] Failed to track event:', error);
  }
}

/**
 * Set user information in Datadog RUM
 */
export function setDatadogUser(user: {
  id: string;
  name?: string;
  email?: string;
  [key: string]: any;
}): void {
  try {
    if (datadogRum) {
      datadogRum.setUser(user);
    }
  } catch (error) {
    console.error('[Datadog] Failed to set user:', error);
  }
}

/**
 * Test Datadog integration
 */
export function testDatadog(): void {
  try {
    if (datadogRum) {
      datadogRum.addAction('test_event', {
        timestamp: new Date().toISOString(),
        test: true
      });
      console.log('[Datadog] Test event sent successfully');
    } else {
      console.log('[Datadog] Not initialized, test skipped');
    }
  } catch (error) {
    console.error('[Datadog] Failed to test:', error);
  }
}

/**
 * Test micro-frontend service extraction
 */
export function testServiceExtraction(): void {
  const testStacks = [
    'Error: Test error\n    at https://staging.appcdn.leadconnectorhq.com/crm/conversations-components/remoteEntry.js:1:1',
    'Error: Test error\n    at https://appcdn.leadconnectorhq.com/revex/phone-integration/remoteEntry.js:1:1',
    'Error: Test error\n    at https://staging.appcdn.leadconnectorhq.com/leadgen/products/remoteEntry.js:1:1',
    'Error: Test error\n    at https://staging.appcdn.leadconnectorhq.com/revex/gokollab-builder--ver--gokollab/remoteEntry.js:1:1',
    'Error: Test error\n    at https://appcdn.leadconnectorhq.com/automation/appointment-modal/remoteEntry.js:1:1',
    'Error: Test error\n    at http://localhost:8080/js/chunk.5085197ea5782e62.js:16247:37',
    'Error: Test error\n    at dispatchXhrRequest @ http://localhost:8080/js/chunk.5085197ea5782e62.js:440056:13'
  ];

  console.log('[Datadog] Testing service extraction:');
  testStacks.forEach((stack, index) => {
    const result = extractServiceFromStack(stack);
    console.log(`Test ${index + 1}:`, result);
  });
}

// Export the config for external use
export { getDatadogConfig };

// Export all functionality as default
export default {
  initializeDatadog,
  addDatadogContext,
  trackDatadogEvent,
  setDatadogUser,
  testDatadog,
  testServiceExtraction,
  getDatadogConfig
}; 